import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAccountDeletion } from '../hooks/useAccountDeletion';
import { useAuth } from '../hooks/useAuth';
import { DeletionConfirmationModal } from '../components/Account/DeletionConfirmationModal';

export const AccountDeletionPage: React.FC = () => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { deleteAccount, isLoading, error } = useAccountDeletion();
  const { user } = useAuth();

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDeletion = async () => {
    try {
      await deleteAccount();
      setShowConfirmation(false);
    } catch (error) {
      // エラーは useAccountDeletion で処理
    }
  };

  // アカウント種別に応じた影響説明
  const getAccountTypeInfo = () => {
    switch (user?.userRole) {
      case 'student':
        return {
          icon: <PersonIcon />,
          title: '学生アカウント',
          color: 'primary' as const,
          impacts: [
            'ログインできなくなります',
            'チャット履歴・投稿履歴は保持されます（管理者確認可能）',
            'スカウトメッセージの受信が停止します',
            'この操作は取り消すことができません'
          ]
        };
      case 'company_admin':
        return {
          icon: <BusinessIcon />,
          title: '企業管理者アカウント',
          color: 'secondary' as const,
          impacts: [
            '⚠️ 企業データごと削除されます',
            '同じ企業に所属する全ユーザー（他の企業管理者含む）が同時に退会されます',
            '企業の全従業員アカウントが退会されます',
            'チャット履歴・投稿履歴は保持されます（管理者確認可能）',
            'この操作は取り消すことができません',
            '個別のアカウント削除は従業員一覧から行ってください'
          ]
        };
      case 'hr':
      case 'employee':
        return {
          icon: <GroupIcon />,
          title: '従業員アカウント',
          color: 'info' as const,
          impacts: [
            '❌ 企業管理者のみが退会できます',
            '個別のアカウント削除は従業員一覧から行ってください',
            '企業管理者が退会した場合、自動的に退会されます',
            'チャット履歴・投稿履歴は保持されます（管理者確認可能）'
          ]
        };
      default:
        return {
          icon: <PersonIcon />,
          title: 'アカウント',
          color: 'default' as const,
          impacts: [
            'ログインできなくなります',
            'データは保持されます（管理者確認可能）',
            'この操作は取り消すことができません'
          ]
        };
    }
  };

  const accountInfo = getAccountTypeInfo();

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <DeleteIcon sx={{ mr: 2, color: 'error.main', fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              アカウント退会
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* アカウント情報表示 */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {accountInfo.icon}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  退会対象アカウント
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={accountInfo.title} 
                  color={accountInfo.color} 
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* 退会影響説明 */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <WarningIcon sx={{ mr: 1, mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  退会すると、以下の影響があります：
                </Typography>
                <List dense>
                  {accountInfo.impacts.map((impact, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Typography variant="body2" color="warning.main">•</Typography>
                      </ListItemIcon>
                      <ListItemText 
                        primary={impact}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          </Alert>

          <Divider sx={{ my: 3 }} />

          {/* 退会ボタン */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={handleDeleteClick}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
              sx={{ 
                px: 4, 
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              {isLoading ? '処理中...' : 'アカウントを退会する'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <DeletionConfirmationModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmDeletion}
        isLoading={isLoading}
        accountType={accountInfo.title}
      />
    </Box>
  );
};
